import { format } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { TFunction } from 'i18next';
import { v4 } from 'uuid';

import {
  ActivityElementsGanttData,
  GanttChartOOAElement,
  GanttChartScheduleElement,
  OutOfAdherenceElementsGanttData,
} from './types';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import {
  AdherenceEvent,
  AgentEvent,
  CommonAdherenceEvent,
  CommonEvent,
  RealTimeActivityEvent,
  RealTimeAdherenceEvent,
  RealTimeAdherenceState,
  ScheduleElement,
  ScheduleItem,
} from '../../graphql/components';
import { formatScheduleElementRGBAColor } from '../../utils/chart';
import { UTC_TIMEZONE_NAME } from '../../utils/date';
import { ganttChartSeriesConfig } from '../AdherenceHistory/config';
import { GanttChartRealityElement } from '../AdherenceHistory/types';
import { approximateScheduleElementNameSize } from '../Scheduling/utils/transformScheduleElementsData';

export const mapScheduleElementsGanttData = (
  t: TFunction,
  scheduleElementsRecordObj: Record<string, ScheduleElement>,
  scheduleItems?: ScheduleItem[]
): GanttChartScheduleElement[] => {
  if (!scheduleItems || !Object.keys(scheduleElementsRecordObj).length) {
    return [];
  }

  return scheduleItems.reduce((elements, scheduleItem) => {
    const sourceScheduleElement = scheduleElementsRecordObj[scheduleItem.scheduleElementId];

    if (!sourceScheduleElement) {
      return elements;
    }

    const startDate = toDate(scheduleItem.dateTimeRange.startDateTime, { timeZone: UTC_TIMEZONE_NAME });
    const endDate = toDate(scheduleItem.dateTimeRange.endDateTime, { timeZone: UTC_TIMEZONE_NAME });

    const start = Number(format(startDate, 'T'));
    const end = Number(format(endDate, 'T'));

    const elementName = translateScheduleElementName(t, sourceScheduleElement);

    return [
      ...elements,
      {
        end,
        start,
        color: formatScheduleElementRGBAColor(sourceScheduleElement.color),
        key: v4(),
        name: elementName,
        nameSize: approximateScheduleElementNameSize(elementName),
        paid: sourceScheduleElement.paid,
        paidTimeOff: sourceScheduleElement.paidTimeOff,
        productive: sourceScheduleElement.productive,
        scheduleElementId: scheduleItem.scheduleElementId,
        y: 1,
      },
    ];
  }, [] as GanttChartScheduleElement[]);
};

export const mapActivityElementsGanttData = (
  t: TFunction,
  timeStamp: number,
  agentEvents?: RealTimeActivityEvent[] | AgentEvent[],
  previousElements: GanttChartRealityElement[] = []
): ActivityElementsGanttData => {
  if (!agentEvents) {
    return {
      elements: [],
    };
  }
  let inProgressAgentActivityIndex: number | undefined;

  const elements = (agentEvents as CommonEvent[]).map(({ id, activityName, from, mappedToActivity, to }, index) => {
    const startDate = toDate(from, { timeZone: UTC_TIMEZONE_NAME });
    const endDate = to ? toDate(to, { timeZone: UTC_TIMEZONE_NAME }) : new Date(timeStamp);

    const start = Number(format(startDate, 'T'));
    const end = Number(format(endDate, 'T'));

    if (!to) {
      inProgressAgentActivityIndex = index + previousElements.length;
    }

    const colorSchemeConfigIndex = mappedToActivity ? 2 : 0; // if element is not mapped to activity, it should be colored with red color - same way as OOA events do

    return {
      end,
      start,
      mappedToActivity,
      color: ganttChartSeriesConfig[colorSchemeConfigIndex].color[(index + previousElements.length) % 2],
      key: id,
      name: activityName,
      nameSize: approximateScheduleElementNameSize(activityName, false), // not all characters are upper-cased!
      y: 2,
    };
  });

  return {
    inProgressAgentActivityIndex,
    elements: previousElements.concat(elements),
  };
};

export const mapOutOfAdherenceElementsGanttData = (
  t: TFunction,
  scheduleElementsRecordObj: Record<string, ScheduleElement>,
  timeStamp: number,
  adherenceEvents?: RealTimeAdherenceEvent[] | AdherenceEvent[],
  previousElements: GanttChartOOAElement[] = []
): OutOfAdherenceElementsGanttData => {
  if (!adherenceEvents) {
    return {
      elements: [],
    };
  }

  let inProgressOOAEventIndex: number | undefined;
  let newTransformedEventsCount = 0;

  const elements = (adherenceEvents as CommonAdherenceEvent[]).reduce(
    (adherenceElements, { id, currentActivity, expectedActivityTypeId, from, state, to }) => {
      const sourceScheduleElement = scheduleElementsRecordObj[expectedActivityTypeId];

      // do not transform and store elements with IN adherence state or when not mapped to schedule element
      if (!sourceScheduleElement || state === RealTimeAdherenceState.In) {
        return adherenceElements;
      }

      const currentItemIndex = newTransformedEventsCount + previousElements.length;
      newTransformedEventsCount += 1;

      const startDate = toDate(from, { timeZone: UTC_TIMEZONE_NAME });
      const endDate = to ? toDate(to, { timeZone: UTC_TIMEZONE_NAME }) : new Date(timeStamp);

      const start = Number(format(startDate, 'T'));
      const end = Number(format(endDate, 'T'));

      const elementName = translateScheduleElementName(t, sourceScheduleElement);

      if (!to) {
        inProgressOOAEventIndex = currentItemIndex;
      }

      return [
        ...adherenceElements,
        {
          end,
          start,
          state,
          activityColor: formatScheduleElementRGBAColor(sourceScheduleElement.color),
          color: ganttChartSeriesConfig[0].color[currentItemIndex % 2],
          key: id,
          name: elementName,
          nameSize: approximateScheduleElementNameSize(elementName),
          statusName: currentActivity,
          y: 0,
        },
      ];
    },
    [] as GanttChartOOAElement[]
  );

  return {
    inProgressOOAEventIndex,
    elements: previousElements.concat(elements),
  };
};
